import {useMemo} from 'react';
import {CellValue, DurationUnit, ReportRow} from '../data/reportRowModel';
import {fieldByKey, ReportTaxonomy} from '../contracts/report-taxonomy.types';

/** A grouping token as the report definition and the catalog spell it.
 *
 * Deliberately a bare string rather than a union of the task grain's eight. The catalog publishes a
 * `groupingValues` list per grain and they do not overlap - a project-status report groups by stage
 * and RAG, which no union of task tokens can name - so a closed union here would either exclude
 * every other grain's tokens or have to be extended for each new one. What a token is ALLOWED to be
 * is answered per grain by `groupingTokensFor`, not by this type. */
export type GroupingToken = string;

/** The task grain's eight tokens and the cell each one groups by. Two of them - `client` and
 * `project` - name something a project-status row also has, under a different path, which is why
 * the map below is resolved per grain rather than shared. */
const TASK_GROUPING_CELL_PATH: Record<string, string> = {
	project: 'task.project.info',
	phase: 'task.phase.name',
	client: 'task.client.name',
	role: 'task.role.info',
	spint: 'task.sprint.name',
	task: 'task.name',
	assignee: 'task.assignees.firstName',
	deliverable: 'task.deliverable.name',
};

/** A grouping token whose cell path a grain does not spell out follows the catalog's own key: the
 * token names a field of the row, and every grain but task keys that field as `<root>.<token>` or
 * `<root>.<token>.name`. Which of the two is decided by the catalog, so a token is resolved against
 * the fields it actually publishes rather than by guessing a suffix. */
function groupingCellPathFrom(taxonomy: ReportTaxonomy | null | undefined, root: string, token: string): string {
	const nested = `${root}.${token}.name`;
	if (taxonomy && fieldByKey(taxonomy, nested)) {
		return nested;
	}
	const bare = `${root}.${token}`;
	if (taxonomy && fieldByKey(taxonomy, bare)) {
		return bare;
	}
	return nested;
}

/** Resolves the token -> cell-path map for a report: the task grain's own eight, or the grain's
 * catalog fields for anything else. A grouping token with no path groups every row into one
 * bucket, so this is the difference between grouping and appearing to group. */
export function groupingCellPaths(root: string, taxonomy?: ReportTaxonomy | null): Record<string, string> {
	if (root === 'task') {
		return TASK_GROUPING_CELL_PATH;
	}
	const paths: Record<string, string> = {};
	for (const token of taxonomy?.groupingValues ?? []) {
		paths[token] = groupingCellPathFrom(taxonomy, root, token);
	}
	return paths;
}

/** The tokens a report of this grain may be grouped by, as the catalog publishes them. An empty
 * list (no taxonomy yet) means nothing is offered rather than the wrong grain's tokens. */
export function groupingTokensFor(root: string, taxonomy?: ReportTaxonomy | null): GroupingToken[] {
	if (root === 'task') {
		return Object.keys(TASK_GROUPING_CELL_PATH);
	}
	return [...(taxonomy?.groupingValues ?? [])];
}

export interface GroupHeaderRow extends ReportRow {
	isGroupHeader: true;
	groupLabel: string;
	groupCount: number;
	/** duration totals per unit; null when units mixed (suppressed) */
	groupTotals: Record<string, {minutes: number; unit: DurationUnit} | null>;
}

/** Fallback 'No <label>' text per task token when the group-defining cell is absent. 'spint'
 * (legacy spelling for 'sprint') gets the corrected 'No sprint' label. */
const NO_GROUP_LABEL: Record<string, string> = {
	project: 'No project',
	phase: 'No phase',
	client: 'No client',
	role: 'No role',
	spint: 'No sprint',
	task: 'No task',
	assignee: 'No assignee',
	deliverable: 'No deliverable',
};

/** The bucket a row with no value for the grouping token falls into. Named from the catalog for a
 * token the task list does not cover, so a project-status report says 'No stage' rather than
 * 'No undefined'. */
function noGroupLabel(token: GroupingToken, cellPath: string, taxonomy?: ReportTaxonomy | null): string {
	const known = NO_GROUP_LABEL[token];
	if (known) {
		return known;
	}
	const label = taxonomy ? fieldByKey(taxonomy, cellPath)?.label : undefined;
	return `No ${(label ?? token).toLowerCase()}`;
}

/** Extracts the display label(s) a row contributes to grouping by `token`. Most cell kinds
 * contribute a single label; the 'persons' cell (used for assignee) contributes one label per
 * person so a multi-assignee row is duplicated across each assignee's group. An empty array
 * means the row falls back to the token's 'No <x>' bucket. */
function labelsForToken(row: ReportRow, token: GroupingToken, cellPaths: Record<string, string>): string[] {
	const cell: CellValue | undefined = row.cells[cellPaths[token]];
	if (cell === undefined) {
		return [];
	}
	switch (cell.kind) {
		case 'persons':
			return cell.persons.map(p => p.name);
		case 'tag':
			// A repeated sub-entity, exactly like 'persons': a row carrying two labels belongs in both
			// of their groups, so it contributes one label per tag and is duplicated across them.
			// Without this case a tag cell fell to the default below and every row landed in the
			// 'No labels' bucket, which reads as a grouping that ran and found nothing.
			return cell.tags.map(tag => tag.name).filter(name => name !== '');
		case 'text':
			return cell.value ? [cell.value] : [];
		case 'project':
			return [cell.name];
		case 'status':
			return [cell.label];
		default:
			return [];
	}
}

/** What grouping needs beyond the rows: which cell each token reads, and the catalog that names a
 * token's empty bucket. */
export interface GroupingOptions {
	cellPaths: Record<string, string>;
	taxonomy?: ReportTaxonomy | null;
}

interface Bucket {
	label: string;
	rows: ReportRow[];
}

/** Buckets top-level `rows` by the labels they contribute for `token`, preserving first-seen
 * label order. A row contributing multiple labels (multi-assignee) is pushed into each of its
 * buckets; a row contributing none goes into the token's 'No <x>' bucket. */
function bucketRows(rows: ReportRow[], token: GroupingToken, options: GroupingOptions): Bucket[] {
	const buckets = new Map<string, Bucket>();
	const fallback = noGroupLabel(token, options.cellPaths[token], options.taxonomy);

	for (const row of rows) {
		const labels = labelsForToken(row, token, options.cellPaths);
		const targetLabels = labels.length > 0 ? labels : [fallback];
		for (const label of targetLabels) {
			let bucket = buckets.get(label);
			if (!bucket) {
				bucket = {label, rows: []};
				buckets.set(label, bucket);
			}
			bucket.rows.push(row);
		}
	}

	return Array.from(buckets.values());
}

/** Sums duration cells at `path` across `rows` (each row's own cell only, not recursing into
 * children). Returns null when zero contributing rows have a duration cell at that path, or when
 * more than one distinct unit is present among contributing rows (cross-unit sums are
 * suppressed, never silently combined). */
function sumDurationsAtPath(rows: ReportRow[], path: string): {minutes: number; unit: DurationUnit} | null {
	let minutes = 0;
	let unit: DurationUnit | null = null;
	let sawMixedUnits = false;
	let contributed = false;

	for (const row of rows) {
		const cell = row.cells[path];
		if (cell === undefined || cell.kind !== 'duration') {
			continue;
		}
		contributed = true;
		if (unit === null) {
			unit = cell.unit;
		} else if (unit !== cell.unit) {
			sawMixedUnits = true;
		}
		minutes += cell.minutes;
	}

	if (!contributed || sawMixedUnits || unit === null) {
		return null;
	}
	return {minutes, unit};
}

/** Returns the row's stable identity for de-dup purposes: the id up to (excluding) the first
 * `::`, which strips the `::<label>` suffix `withDecollidedId` adds to multi-assignee duplicates.
 * A task duplicated across several assignee sub-groups shares this base id across all its copies. */
function baseRowId(row: ReportRow): string {
	const suffixIndex = row.id.indexOf('::');
	return suffixIndex === -1 ? row.id : row.id.slice(0, suffixIndex);
}

/** Recursively collects the descendant leaf DATA rows under `rows` for group-totalling purposes:
 * recurses through `GroupHeaderRow.children` (synthetic nesting levels) but treats any real
 * `ReportRow` as a leaf and does not descend into its own subtask/deliverable `children` — matches
 * the pre-existing (unreviewed-as-wrong) leaf-level behaviour of only totalling each data row's own
 * cells, not its hierarchy's. De-duplicates by base id (see `baseRowId`) so a multi-assignee task
 * duplicated across several assignee sub-groups is counted once toward an ancestor total. */
function collectDescendantDataRows(rows: ReportRow[]): ReportRow[] {
	const seen = new Set<string>();
	const result: ReportRow[] = [];

	function visit(row: ReportRow): void {
		if ((row as GroupHeaderRow).isGroupHeader === true) {
			for (const child of row.children) {
				visit(child);
			}
			return;
		}
		const id = baseRowId(row);
		if (seen.has(id)) {
			return;
		}
		seen.add(id);
		result.push(row);
	}

	for (const row of rows) {
		visit(row);
	}
	return result;
}

/** Computes per-column duration totals for a group, from its descendant leaf data rows (see
 * `collectDescendantDataRows`) — not the group's immediate `children`, which for any non-leaf
 * nesting level are themselves synthetic `GroupHeaderRow`s with empty `cells` and would otherwise
 * make every ancestor level's totals silently empty. Totals across every field path that appears as
 * a duration cell on at least one contributing leaf row. */
function computeGroupTotals(children: ReportRow[]): Record<string, {minutes: number; unit: DurationUnit} | null> {
	const rows = collectDescendantDataRows(children);

	const paths = new Set<string>();
	for (const row of rows) {
		for (const [path, cell] of Object.entries(row.cells)) {
			if (cell.kind === 'duration') {
				paths.add(path);
			}
		}
	}

	const totals: Record<string, {minutes: number; unit: DurationUnit} | null> = {};
	for (const path of paths) {
		totals[path] = sumDurationsAtPath(rows, path);
	}
	return totals;
}

/** Builds a group header pseudo-row. Its id embeds the full token/label path so nested grouping
 * levels (and repeated labels across sibling branches, e.g. the same assignee under two
 * different projects) never collide. It satisfies `ReportRow` so Task 3's table renders its
 * `children` through the same subrow mechanism as real rows. */
function buildGroupHeaderRow(idPath: string, token: GroupingToken, label: string, children: ReportRow[]): GroupHeaderRow {
	return {
		id: `group:${token}:${idPath}`,
		type: 'task',
		parentId: null,
		// A synthetic grouping header is not a task, so there is nothing to open.
		companyTaskId: null,
		children,
		matchedFilter: true,
		favorite: false,
		cells: {},
		isGroupHeader: true,
		groupLabel: label,
		// Descendant leaf DATA rows, not immediate `children`: at any non-leaf grouping level the
		// immediate children are themselves synthetic group headers, so `children.length` would
		// report the sub-group count instead of the number of tasks under this group.
		groupCount: collectDescendantDataRows(children).length,
		groupTotals: computeGroupTotals(children),
	};
}

/** De-collides a row's id for the group branch it has been duplicated into. Only rows that
 * contribute more than one label for the current token (multi-assignee tasks) are actually
 * duplicated across branches; giving every duplicate a `::<groupLabel>` suffix keeps React keys
 * and TanStack's internal row ids unique even though the underlying row object is shared across
 * groups. Children keep their original ids since they are not independently duplicated. */
function withDecollidedId(row: ReportRow, groupLabel: string, duplicated: boolean): ReportRow {
	if (!duplicated) {
		return row;
	}
	return {...row, id: `${row.id}::${groupLabel}`};
}

/** Replaces each top-level deliverable row with its children. A deliverable row is a structural
 * container report-service promotes from the tasks beneath it (the real hierarchy is project →
 * deliverable → task → subtask); it carries no task cells of its own, so bucketing it directly
 * drops every task beneath it into the token's 'No <x>' bucket. Only the top level is unwrapped —
 * `promoteDeliverables` produces deliverable rows only there, and grouping never descended into
 * `children` anyway. The unwrapped tasks keep a `parentId` pointing at the row that no longer
 * exists; nothing in the table reads `parentId` (nesting is rendered from `children`), and the
 * pre-existing multi-assignee duplication already breaks that link the same way. Returns `rows`
 * unchanged when there is nothing to unwrap. */
function unwrapDeliverableRows(rows: ReportRow[]): ReportRow[] {
	if (!rows.some(row => row.type === 'deliverable')) {
		return rows;
	}
	return rows.flatMap(row => (row.type === 'deliverable' ? row.children : [row]));
}

/** Recursively groups `rows` by the ordered `grouping` tokens (max 4 levels). Only top-level
 * rows are distributed into groups at each level; each row's own `children` (subtasks/deliverables)
 * stay attached beneath it, untouched, inside whichever group it lands in. */
function groupRowsRecursive(
	rows: ReportRow[],
	grouping: GroupingToken[],
	idPathPrefix: string,
	options: GroupingOptions
): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}

	const [token, ...restTokens] = grouping;
	const buckets = bucketRows(rows, token, options);

	return buckets.map(bucket => {
		const branchRows = bucket.rows.map(row => {
			const rowDuplicated = labelsForToken(row, token, options.cellPaths).length > 1;
			return withDecollidedId(row, bucket.label, rowDuplicated);
		});
		const idPath = idPathPrefix ? `${idPathPrefix}/${bucket.label}` : bucket.label;
		const nestedChildren = groupRowsRecursive(branchRows, restTokens, idPath, options);
		return buildGroupHeaderRow(idPath, token, bucket.label, nestedChildren);
	});
}

/** Pure grouping transform: groups only top-level `rows` (never descends into `children`) by the
 * ordered `grouping` tokens, nesting group header pseudo-rows up to 4 levels deep. Duplicates
 * multi-assignee rows across each contributing assignee's group. Returns `rows` unchanged (same
 * reference) when `grouping` is empty. */
export function groupRows(rows: ReportRow[], grouping: GroupingToken[], options?: Partial<GroupingOptions>): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}
	const resolved: GroupingOptions = {
		cellPaths: options?.cellPaths ?? TASK_GROUPING_CELL_PATH,
		taxonomy: options?.taxonomy,
	};
	// A token the grain does not publish has no cell to read, so every row would land in one bucket
	// under a heading naming a field the report has not got. Dropping it groups by what remains,
	// which is what the reader asked for minus the part that could never have worked.
	const known = grouping.filter(token => resolved.cellPaths[token] !== undefined);
	// definition contract caps grouping at 4 levels regardless of how many tokens the caller passes.
	const cappedGrouping = known.slice(0, 4);
	if (cappedGrouping.length === 0) {
		return rows;
	}
	return groupRowsRecursive(unwrapDeliverableRows(rows), cappedGrouping, '', resolved);
}

/** Thin memoization wrapper around `groupRows` for use in table components. */
export function useClientGrouping(
	rows: ReportRow[],
	grouping: GroupingToken[],
	options?: Partial<GroupingOptions>
): ReportRow[] {
	const cellPaths = options?.cellPaths;
	const taxonomy = options?.taxonomy;
	return useMemo(() => groupRows(rows, grouping, {cellPaths, taxonomy}), [rows, grouping, cellPaths, taxonomy]);
}
